/**
 * Экран сервиса машины: техническое состояние, переводы цикла ремонта и
 * сервисная история. Это **не карточка машины**: здесь опознание — вид, VIN,
 * реквизит техпаспорта, состояние — и ничего сверх: ни фото, ни раздела
 * неисправностей, ни комментариев.
 *
 * Главный риск блока — цикл ремонта. Экран предлагает **весь порождённый
 * перечень состояний целиком, включая текущее**, и не вычисляет допустимых
 * переходов ни в каком виде: у ответа нет поля с переходами, клиент цикла не
 * знает и знать не может. Нажатие уходит на сервер всегда; отказ приходит
 * статусом 422 и показывается на месте, состояние на экране берётся **из
 * ответа**, а не из нажатого значения.
 *
 * Сервисная запись идемпотентна **заголовком**: ключ рождается в сетевом слое
 * на каждую отправку заново, две одинаковые записи законны, и клиент второй не
 * подавляет. Деньги ходят строкой как пришли: перевода в число и итогов по
 * журналу здесь нет — расход фирмы на ремонт не цена фрахта, но складывать его
 * не просит ни одно требование.
 *
 * ПЕРЕВОРОТ блока `WEB-2.4` (решения формы `Б13`, `Б14`; контракт `0.9.33`).
 * Было: «заменённые узлы» — одно поле `parts_replaced` строкой. Стало: «что
 * сделано, какие узлы» — описание, а запчасти — строками «название,
 * количество», списком `parts`. Поля `parts_replaced` на форме нет — сервер
 * его в запросе не принимает, — и склейка «узлов» в описание новой строкой,
 * которой ветка `BE-2.32` закрывала окно поломки, снята: описание уходит
 * ровно введённым. Строки называются ключами сообщений сервера
 * (`parts.<i>.name`, `parts.<i>.quantity`), количество уходит числом
 * введённого без проверки целого и пределов, а строк клиент не считает —
 * пределы судит сервер. Форма открывается без пустой строки: строку добавляет
 * человек. В журнале у записи — каждая строка `parts` в порядке ответа и
 * строка `parts_replaced` прежних записей как пришла: ни разбора, ни склейки.
 *
 * Журнал только пополняется: автора записи нет намеренно — «кто внёс» придёт
 * слоем аудита, — а операций правки и удаления не существует вовсе.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient } from '../api/client';
import type {
  RepairStateChangeRequest,
  ServicePart,
  ServiceRecord,
  ServiceRecordCreateRequest,
  Vehicle,
} from '../api/contract';
import { REPAIR_STATES } from '../api/generated/enums';
import { activeLocale, repairStateStringKey, vehicleKindStringKey } from '../i18n';
import type { Strings } from '../i18n';
import { vehiclePath } from '../router';
import {
  createFieldMessageSlot,
  enumLabel,
  momentLabel,
  renderFieldMessages,
  renderScreenState,
  screenStateFor,
  selectEnumOption,
} from './state';
import type { ScreenState } from './state';

export interface VehicleServiceScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
  readonly vehicleId: string;
}

/** Строка запчасти, как её ввёл человек: название и количество строками. */
export interface ServicePartInput {
  readonly name: string;
  readonly quantity: string;
}

/** Введённое человеком — ровно поля тела сервисной записи, и ни одного сверх. */
/**
 * Уходит ли строка запчасти в тело: строка с обоими пустыми полями не уходит
 * (решение формы `Б13`). Одно правило на тело и на имена полей формы — имена
 * строк, ушедших в тело, равны их номерам **в теле** (ответ на ревью Р1).
 */
function partIsSent(part: ServicePartInput): boolean {
  return part.name !== '' || part.quantity !== '';
}

export interface ServiceRecordInput {
  readonly description: string;
  readonly parts?: readonly ServicePartInput[];
  readonly costAmount?: string | null;
  readonly costCurrency?: string | null;
}

export interface VehicleServiceScreen {
  readonly element: HTMLElement;
  readonly state: ScreenState | null;
  readonly records: readonly ServiceRecord[];
  load(): Promise<void>;
  changeState(chosen: string): Promise<void>;
  addRecord(input: ServiceRecordInput): Promise<void>;
  /** Переподписать экран на новом языке, не трогая введённого (долг `TD-72`). */
  relabel(next: Strings): void;
}

export function createVehicleServiceScreen(
  options: VehicleServiceScreenOptions,
): VehicleServiceScreen {
  const { client, companyId, vehicleId } = options;
  let { strings } = options;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const identity = document.createElement('p');
  const stateChangeForm = document.createElement('form');
  const stateCaption = document.createElement('label');
  const stateCaptionText = document.createElement('span');
  const stateField = document.createElement('select');
  const stateMessage = createFieldMessageSlot();
  const stateSubmit = document.createElement('button');
  const recordsHeading = document.createElement('h2');
  const journalList = document.createElement('ul');
  const journalEmpty = document.createElement('p');
  const recordForm = document.createElement('form');
  const descriptionCaption = document.createElement('label');
  const descriptionCaptionText = document.createElement('span');
  const descriptionField = document.createElement('input');
  const descriptionMessage = createFieldMessageSlot();
  const partsGroup = document.createElement('fieldset');
  const partsLegend = document.createElement('legend');
  const partsList = document.createElement('ul');
  const partAdd = document.createElement('button');
  const amountCaption = document.createElement('label');
  const amountCaptionText = document.createElement('span');
  const amountField = document.createElement('input');
  const amountMessage = createFieldMessageSlot();
  const currencyCaption = document.createElement('label');
  const currencyCaptionText = document.createElement('span');
  const currencyField = document.createElement('input');
  const currencyMessage = createFieldMessageSlot();
  const recordSubmit = document.createElement('button');
  const cardLink = document.createElement('a');
  /** Место сообщения сервера, которому не нашлось поля, — форма members.ts. */
  const notice = createFieldMessageSlot();

  element.className = 'screen screen-vehicle-service';
  identity.className = 'vehicle-identity';
  stateChangeForm.className = 'form';
  stateCaption.className = 'form-field';
  stateCaptionText.className = 'form-field__caption';
  recordForm.className = 'form';
  descriptionCaption.className = 'form-field';
  descriptionCaptionText.className = 'form-field__caption';
  partsGroup.className = 'form-field service-parts';
  partsList.className = 'service-parts__list';
  partAdd.className = 'action';
  partAdd.type = 'button';
  amountCaption.className = 'form-field';
  amountCaptionText.className = 'form-field__caption';
  currencyCaption.className = 'form-field';
  currencyCaptionText.className = 'form-field__caption';
  stateSubmit.className = 'action';
  recordSubmit.className = 'action';
  journalEmpty.className = 'list-empty';

  stateSubmit.type = 'submit';
  recordSubmit.type = 'submit';

  // Карточка машины — блок `WEB-2.4`, решение формы `Б2`: экран сервиса и
  // карточка ссылаются друг на друга. Адрес собирает единственный строитель.
  cardLink.className = 'company-link';
  cardLink.href = vehiclePath(companyId, vehicleId);

  /** Подписи экрана — одним местом: смена языка переподписывает их на месте. */
  function caption(): void {
    heading.textContent = strings.service_title;
    stateCaptionText.textContent = strings.service_state_label;
    stateSubmit.textContent = strings.service_state_change;
    recordsHeading.textContent = strings.service_records_title;
    journalEmpty.textContent = strings.service_records_empty;
    descriptionCaptionText.textContent = strings.service_description;
    partsLegend.textContent = strings.service_parts;
    partAdd.textContent = strings.service_part_add;
    amountCaptionText.textContent = strings.service_cost;
    currencyCaptionText.textContent = strings.service_currency;
    recordSubmit.textContent = strings.service_record_add;
    cardLink.textContent = strings.vehicle_card_title;

    for (const option of stateField.querySelectorAll('option')) {
      option.textContent = enumLabel(strings, repairStateStringKey(option.value));
    }

    for (const part of partRows) {
      part.caption();
    }
  }

  // Имена полей — имена полей тел запросов: по ним сервер называет место
  // своего сообщения. Перечень полей закрыт составом схем — второго денежного
  // поля здесь нет и появиться не может; запчасти — строками `parts.<i>.*`.
  stateField.name = 'state';
  descriptionField.name = 'description';
  amountField.name = 'cost_amount';
  currencyField.name = 'cost_currency';

  // Набор предлагаемых состояний — порождённый перечень **целиком**, включая
  // текущее: сокращение набора было бы перечнем переходов в клиенте, только
  // записанным вычитанием. Отказ на недопустимый переход приходит от сервера.
  for (const state of REPAIR_STATES) {
    const choice = document.createElement('option');

    choice.value = state;
    stateField.append(choice);
  }

  /** Строка запчасти на форме: два поля, у каждого — место сообщения сервера. */
  interface PartRow {
    readonly element: HTMLElement;
    readonly nameField: HTMLInputElement;
    readonly quantityField: HTMLInputElement;
    /** Имена полей `parts.<i>.name` и `parts.<i>.quantity`: по месту строки, на отправке — по номеру в теле. */
    rename(index: number): void;
    caption(): void;
  }

  /**
   * Строки запчастей в порядке ввода. Форма открывается без строки (ответ
   * ведущего `В3`): строку добавляет человек кнопкой «Добавить строку».
   */
  let partRows: PartRow[] = [];

  function createPartRow(): PartRow {
    const element = document.createElement('li');
    const nameCaption = document.createElement('label');
    const nameCaptionText = document.createElement('span');
    const nameField = document.createElement('input');
    const nameMessage = createFieldMessageSlot();
    const quantityCaption = document.createElement('label');
    const quantityCaptionText = document.createElement('span');
    const quantityField = document.createElement('input');
    const quantityMessage = createFieldMessageSlot();
    const remove = document.createElement('button');

    element.className = 'service-part';
    nameCaption.className = 'form-field';
    nameCaptionText.className = 'form-field__caption';
    quantityCaption.className = 'form-field';
    quantityCaptionText.className = 'form-field__caption';
    // Количество — текстом, а не `type=number`: поле числа браузера само
    // отбрасывает «неправильное», и пределы судил бы клиент, а не сервер.
    quantityField.inputMode = 'numeric';
    remove.type = 'button';
    remove.className = 'action';

    nameCaption.append(nameCaptionText, nameField, nameMessage);
    quantityCaption.append(quantityCaptionText, quantityField, quantityMessage);
    element.append(nameCaption, quantityCaption, remove);

    const row: PartRow = {
      element,
      nameField,
      quantityField,
      rename(index: number): void {
        nameField.name = `parts.${index}.name`;
        quantityField.name = `parts.${index}.quantity`;
      },
      caption(): void {
        nameCaptionText.textContent = strings.service_part_name;
        quantityCaptionText.textContent = strings.service_part_quantity;
        remove.textContent = strings.service_part_remove;
      },
    };

    remove.addEventListener('click', () => {
      removePartRow(row);
    });
    row.caption();

    return row;
  }

  /**
   * Имена строк на минуту отправки — по номеру **в теле** (ответ на ревью Р1,
   * поправка CONTRACT К2): пустая строка в тело не уходит, и номер строки в
   * теле расходится с её местом на форме. Ключ сообщения сервера `parts.N.*`
   * называет строку тела, и сообщение ложится под ту строку формы, что ушла
   * под номером N. Неушедшие строки получают номера после ушедших: имена
   * остаются единственными, а ключа с таким номером сервер не назовёт — в
   * теле его нет. Имя поля в коде не разбирается — сопоставляет по-прежнему
   * `renderFieldMessages`.
   */
  function nameRowsAsSent(): void {
    const skipped: PartRow[] = [];
    let index = 0;

    for (const row of partRows) {
      if (partIsSent({ name: row.nameField.value, quantity: row.quantityField.value })) {
        row.rename(index);
        index += 1;
      } else {
        skipped.push(row);
      }
    }

    for (const row of skipped) {
      row.rename(index);
      index += 1;
    }
  }

  /** Строки встают на форму заново и переименовываются по месту. */
  function placePartRows(): void {
    partRows.forEach((row, index) => {
      row.rename(index);
    });
    partsList.replaceChildren(...partRows.map((row) => row.element));
  }

  function removePartRow(removed: PartRow): void {
    const kept: PartRow[] = [];

    for (const row of partRows) {
      if (row !== removed) {
        kept.push(row);
      }
    }

    partRows = kept;
    placePartRows();
  }

  partAdd.addEventListener('click', () => {
    partRows = [...partRows, createPartRow()];
    placePartRows();
  });

  caption();

  stateCaption.append(stateCaptionText, stateField, stateMessage);
  stateChangeForm.append(stateCaption, stateSubmit);
  descriptionCaption.append(descriptionCaptionText, descriptionField, descriptionMessage);
  partsGroup.append(partsLegend, partsList, partAdd);
  amountCaption.append(amountCaptionText, amountField, amountMessage);
  currencyCaption.append(currencyCaptionText, currencyField, currencyMessage);
  recordForm.append(descriptionCaption, partsGroup, amountCaption, currencyCaption, recordSubmit);

  let state: ScreenState | null = 'loading';
  let vehicle: Vehicle | null = null;
  let records: readonly ServiceRecord[] = [];
  /** Пришёл ли ответ сервера — та же величина, что на экране фирмы. */
  let answered = false;

  /** Строка запчасти журнала: название и количество, как пришли. */
  function partLine(part: ServicePart): HTMLElement {
    const node = document.createElement('li');

    node.textContent = `${part.name} — ${part.quantity}`;

    return node;
  }

  function recordRow(record: ServiceRecord): HTMLElement {
    const node = document.createElement('li');

    node.className = 'service-record-row';

    // Строки запчастей — каждая, в порядке ответа: «название — количество».
    // Строка узлов прежней записи — одной строкой как пришла: клиент её не
    // разбирает и не склеивает. Расход — строкой как пришёл: в число деньги не
    // переводятся и не складываются.
    const line = document.createElement('span');
    const partsShown = record.parts_replaced ?? '';
    const amountShown = record.cost_amount ?? '';
    const currencyShown = record.cost_currency ?? '';

    line.textContent = [
      momentLabel(activeLocale(), record.created_at),
      record.description,
    ].join(' ');
    node.append(line);

    if (record.parts.length > 0) {
      const parts = document.createElement('ul');

      parts.className = 'service-record-row__parts';

      for (const part of record.parts) {
        parts.append(partLine(part));
      }

      node.append(parts);
    }

    const tail = document.createElement('span');

    tail.textContent = [partsShown, amountShown, currencyShown].join(' ');
    node.append(tail);

    return node;
  }

  function render(): void {
    notice.textContent = '';

    if (state !== null && !answered) {
      // Ответа нет: показывать нечего вовсе, отказ прав и отсутствие объекта
      // дают побайтово один экран — нераскрытие.
      element.replaceChildren(renderScreenState(state, strings));

      return;
    }

    if (vehicle === null) {
      // Список пришёл, а машины с данным адресом в нём нет: содержимого не
      // существует, и это отказ экрана целиком.
      element.replaceChildren(renderScreenState(screenStateFor({ kind: 'failed', code: null }) ?? 'error', strings));

      return;
    }

    // Опознание машины: журнал без него безымянен. Вид и состояние — подписью
    // по строителю, и ничего сверх: фото и неисправности — не этот экран.
    identity.textContent = [
      enumLabel(strings, vehicleKindStringKey(vehicle.kind)),
      vehicle.vin,
      vehicle.tech_passport_number,
      enumLabel(strings, repairStateStringKey(vehicle.repair_state)),
    ].join(' ');

    // Выбранным помечается состояние **из ответа**, а не нажатое значение.
    selectEnumOption(stateField, repairStateStringKey(vehicle.repair_state), repairStateStringKey);

    const rows: HTMLElement[] = [];

    for (const record of records) {
      rows.push(recordRow(record));
    }

    journalList.replaceChildren(...rows);
    // Состояние «пусто» принадлежит **журналу**, а не экрану: пустой журнал —
    // пришедший ответ, и форма записи в нём остаётся.
    element.replaceChildren(
      heading,
      identity,
      cardLink,
      notice,
      stateChangeForm,
      recordsHeading,
      records.length === 0 ? journalEmpty : journalList,
      recordForm,
    );
  }

  /**
   * Исход неудачного **действия** — форма member-rights.ts: осмысленный отказ
   * сервера остаётся на экране с содержимым и показывается его словами.
   */
  function actionFailed(form: HTMLElement, error: unknown): void {
    const failure = error instanceof ApiFailure ? error : null;
    const asScreen = screenStateFor({
      kind: 'failed',
      code: failure === null ? null : failure.code,
    });

    if (failure === null || failure.code === null || asScreen === 'noAccess') {
      state = asScreen;
      answered = false;
      render();

      return;
    }

    render();

    const homeless = renderFieldMessages(form, failure.details);
    const texts: string[] = [];

    for (const detail of homeless) {
      texts.push(detail.message);
    }

    if (texts.length === 0 && failure.reason !== null) {
      texts.push(failure.reason);
    }

    notice.textContent = texts.join(' ');
  }

  /** Запись реестра с данным адресом — из списка фирмы, второй операции нет. */
  function vehicleIn(fleet: readonly Vehicle[]): Vehicle | null {
    for (const known of fleet) {
      if (known.id === vehicleId) {
        return known;
      }
    }

    return null;
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    vehicle = null;
    records = [];
    answered = false;
    render();

    try {
      const fleet = await client.listCompanyVehicles(companyId);
      const journal = await client.listVehicleServiceRecords(companyId, vehicleId);

      vehicle = vehicleIn(fleet);
      records = journal;
      answered = vehicle !== null;
      state = vehicle === null
        ? screenStateFor({ kind: 'failed', code: null })
        : screenStateFor({ kind: 'done', empty: false });
    } catch (error) {
      vehicle = null;
      records = [];
      answered = false;
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  async function changeState(chosen: string): Promise<void> {
    renderFieldMessages(stateChangeForm, []);

    // Нажатие уходит на сервер всегда: ни один из отказов клиент не
    // предсказывает — повтор текущего состояния тоже решает сервер.
    try {
      const received = await client.changeVehicleRepairState(companyId, vehicleId, {
        state: chosen as RepairStateChangeRequest['state'],
      });

      // Экран перерисовывается по ответу — тот несёт запись целиком, и
      // состояние на экране это состояние сервера, а не нажатое значение.
      vehicle = received;
      state = null;
      render();
    } catch (error) {
      actionFailed(stateChangeForm, error);
    }
  }

  async function addRecord(input: ServiceRecordInput): Promise<void> {
    renderFieldMessages(recordForm, []);

    // Тело — ровно поля ServiceRecordCreateRequest: описание — ровно
    // введённым, запчасти — списком строк, расход — строками как введены.
    // Строка с обоими пустыми полями не уходит; без строк поля `parts` нет
    // вовсе — пустой список даёт сервер. Количество — числом введённого, без
    // проверки целого и пределов: `0`, `1.5` и пустое при названии судит
    // сервер (`422` под полем строки).
    const body: ServiceRecordCreateRequest = { description: input.description };
    const parts: ServicePart[] = [];

    for (const part of input.parts ?? []) {
      if (partIsSent(part)) {
        parts.push({ name: part.name, quantity: Number(part.quantity) });
      }
    }

    if (parts.length > 0) {
      body.parts = parts;
    }

    if (typeof input.costAmount === 'string' && input.costAmount !== '') {
      body.cost_amount = input.costAmount;
    }

    if (typeof input.costCurrency === 'string' && input.costCurrency !== '') {
      body.cost_currency = input.costCurrency;
    }

    try {
      const received = await client.addVehicleServiceRecord(companyId, vehicleId, body);

      // Журнал пополняется записью ответа: две одинаковые записи законны, и
      // вторая появляется на экране так же, как первая.
      records = [...records, received];
      state = null;
      render();
    } catch (error) {
      actionFailed(recordForm, error);
    }
  }

  stateChangeForm.addEventListener('submit', (event) => {
    event.preventDefault();

    void changeState(stateField.value);
  });

  recordForm.addEventListener('submit', (event) => {
    event.preventDefault();

    const parts: ServicePartInput[] = [];

    for (const row of partRows) {
      parts.push({ name: row.nameField.value, quantity: row.quantityField.value });
    }

    nameRowsAsSent();

    void addRecord({
      description: descriptionField.value,
      parts,
      costAmount: amountField.value === '' ? null : amountField.value,
      costCurrency: currencyField.value === '' ? null : currencyField.value,
    });
  });

  /**
   * Переподписать экран на новом языке (долг `TD-72`, блок `WEB-2.4`): поля
   * формы и строки запчастей остаются теми же узлами с введённым, меняются
   * только подписи; журнал перерисовывается из того, что уже пришло.
   */
  function relabel(next: Strings): void {
    strings = next;
    caption();
    render();
  }

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    get records(): readonly ServiceRecord[] {
      return records;
    },

    load,
    changeState,
    addRecord,
    relabel,
  };
}
